<script setup lang="ts">
import { shareNative } from '~/shared/NativeBridge';

type Props = {
  gameDetails: Record<string, any>;
  statistics?: GameStatisticsByPeriod | null;
};

const { gameDetails, statistics } = defineProps<Props>();

type Emits = {
  'full-screen': [];
};

const emit = defineEmits<Emits>();

const gameDemoMode = defineModel<Game.Mode>('gameDemoMode', {
  default: 'real',
});

const { isAuthenticated } = storeToRefs(useAuthStore());
const { isDemoModeEnabled, isRTPEnabled } = useFeatureFlagsNew();
const { openToaster } = useLayoutStore();
const { t } = useTexts();

const rtpAverage = computed<number>(() => {
  return Math.round(statistics?.rtp_last_30_days || 0);
});

const isRtpBarLoading = computed<boolean>(() => {
  return gameDetails.isLoading || statistics === null;
});

const isRtpBarVisible = computed<boolean>(() => {
  return rtpAverage.value > 0;
});

type SharePlatform =
  | 'facebook'
  | 'whatsapp'
  | 'twitter'
  | 'telegram'
  | 'native';

const isSharing = ref(false);

function buildSharerUrl(platform: SharePlatform): string {
  const url = encodeURIComponent(window.location.href);
  const text = encodeURIComponent(`Venha jogar ${gameDetails.name}!`);

  switch (platform) {
    case 'whatsapp':
      return `https://api.whatsapp.com/send?text=${text}%20${url}`;

    case 'telegram':
      return `https://t.me/share/url?url=${url}&text=${text}`;

    case 'twitter':
      return `https://twitter.com/intent/tweet?url=${url}&text=${text}`;

    case 'facebook':
      return `https://www.facebook.com/sharer/sharer.php?u=${url}`;

    default:
      return '';
  }
}

async function shareGame(platform: SharePlatform = 'native') {
  if (isSharing.value) return;
  isSharing.value = true;

  try {
    if (platform === 'native') {
      const outcome = await shareNative({
        title: `${gameDetails.name} | ${appName()}`,
        message: `Venha jogar ${gameDetails.name}!`,
        url: window.location.href,
      });

      if (outcome === 'navigator') {
        openToaster({
          message: t('casino.game.sharing.success'),
          type: 'success',
        });
      } else if (outcome === 'unsupported') {
        openToaster({
          message: t('casino.game.sharing.not_supported'),
          type: 'error',
        });
      }

      return;
    }

    const webUrl = buildSharerUrl(platform);

    if (!webUrl) return;

    window.open(webUrl, '_blank', 'noopener,noreferrer');
  } finally {
    setTimeout(() => {
      isSharing.value = false;
    }, 400);
  }
}

const shareIcons: { icon: string; platform: SharePlatform }[] = [
  { icon: 'social/facebook', platform: 'facebook' },
  { icon: 'social/whatsapp', platform: 'whatsapp' },
  { icon: 'social/x', platform: 'twitter' },
  { icon: 'social/telegram', platform: 'telegram' },
  { icon: 'share', platform: 'native' },
];

const isGameDemoToggleVisible = computed<boolean>(() => {
  return !!gameDetails.demo && !!isDemoModeEnabled.value;
});

const isGameRTPVisible = computed<boolean>(() => {
  return !!gameDetails.rtp && !!isRTPEnabled.value && !!isAuthenticated.value;
});

function onCasinoButtonClick() {
  emit('full-screen');
}

const rtpInvertValue = computed(() => {
  const rtp = String(gameDetails.rtp ?? 0).replace('%', '');

  return `${100 - Number(rtp)}%`;
});
</script>

<template>
  <section :class="$style.gameSectionDetails">
    <div :class="$style.topRow">
      <div class="w-full">
        <div :class="$style.gameTitleContainer">
          <div>
            <div
              v-if="gameDetails.isLoading"
              :class="[`skeleton-loading`, $style.skeleton]"
            />
            <h1 v-else>{{ gameDetails.name }}</h1>
          </div>
        </div>

        <div :class="$style.providerName">
          <div
            v-if="gameDetails.isLoading"
            :class="[`skeleton-loading`, $style.skeleton]"
          />
          <span v-else>
            {{ gameDetails.provider ? gameDetails.provider : 'Sem Provedor' }}
          </span>
        </div>
      </div>

      <div :class="$style.detailsButtons">
        <template v-if="gameDetails.isLoading">
          <div :class="[`skeleton-loading`, $style.skeleton]" />
          <div :class="[`skeleton-loading`, $style.skeleton]" />
          <div :class="[`skeleton-loading`, $style.skeleton]" />
          <div :class="[`skeleton-loading`, $style.skeleton]" />
        </template>

        <template v-else>
          <template v-if="isGameRTPVisible">
            <button popovertarget="equityDialog">
              <NuxtIcon name="scales" />
            </button>

            <div
              id="equityDialog"
              :class="$style.equityDialog"
              popover
            >
              <div :class="$style.equityValue">
                <span>
                  {{ $t('casino.game.house_edge', { value: rtpInvertValue }) }}
                </span>
              </div>
            </div>
          </template>

          <CasinoGameDemoToggle
            v-if="isGameDemoToggleVisible"
            v-model="gameDemoMode"
            :disabled="gameDetails.isLoading"
          />

          <CasinoVotesApostatudo
            v-if="!gameDetails.isLoading"
            :casino-game-id="gameDetails.id"
          />

          <div :class="$style.shareIcons">
            <button
              v-for="item in shareIcons"
              :key="item.icon"
              :class="$style.shareIconBtn"
              :disabled="isSharing"
              type="button"
              @click="shareGame(item.platform)"
            >
              <NuxtIcon :name="item.icon" />
            </button>
          </div>

          <button
            v-if="!gameDetails.error"
            :class="$style.fullscreenBtn"
            @click="onCasinoButtonClick"
          >
            <NuxtIcon name="expand" />
          </button>
        </template>
      </div>
    </div>

    <div
      v-if="isRtpBarLoading"
      :class="[`skeleton-loading`, $style.rtpBarSkeleton]"
    />
    <div
      v-else-if="isRtpBarVisible"
      :class="$style.rtpBar"
    >
      <div
        :class="$style.rtpBarFill"
        :style="`width: ${rtpAverage}%`"
      ></div>
      <span :class="$style.rtpBarLabel">RTP MÉDIO: {{ rtpAverage }}%</span>
    </div>
  </section>
</template>

<style module lang="postcss">
.gameSectionDetails {
  @apply hidden md:flex md:flex-col w-full mb-5 p-5 overflow-hidden text-texts border border-solid border-white/10 border-t-0;

  border-radius: 0 0 10px 10px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);

  :global(.favorite-button),
  :global(.favorite-button:hover:not(:disabled)) {
    background: transparent;
    backdrop-filter: none;
  }

  .topRow {
    @apply flex items-start justify-between w-full gap-4;
  }

  .shareIcons {
    @apply flex items-center gap-1.5 ml-1;

    .shareIconBtn {
      @apply flex items-center justify-center w-7 h-7 rounded-full bg-white/10 p-0 text-white hover:bg-white/20 transition-colors;

      svg {
        @apply w-[18px] h-[18px] text-white opacity-80 grayscale;
      }
    }
  }

  .fullscreenBtn {
    @apply flex items-center justify-center w-7 h-7 rounded-full bg-white/10 p-0 text-white hover:bg-white/20 transition-colors ml-1;

    svg {
      @apply w-3.5 h-3.5 text-white;
    }
  }

  .rtpBarSkeleton {
    width: 100%;
    min-height: 18px;
    border-radius: 100px;
    margin-top: 12px;
  }

  .rtpBar {
    position: relative;
    display: flex;
    padding: 1px 15px;
    justify-content: flex-end;
    align-items: center;
    gap: 10px;
    flex: 1 0 0;
    align-self: stretch;
    border-radius: 100px;
    background: rgba(255, 255, 255, 0.08);
    overflow: hidden;
    margin-top: 12px;
    min-height: 18px;

    .rtpBarFill {
      position: absolute;
      top: 0;
      left: 0;
      height: 100%;
      border-radius: 100px;
      background: radial-gradient(
        66.67% 66.67% at 51.11% 51.11%,
        #00c9ff 49.5%,
        #0062ff 100%
      );
      transition: width 0.3s ease;
    }

    .rtpBarLabel {
      @apply text-white text-[10px] font-extrabold tracking-wide;

      position: relative;
      z-index: 1;
    }
  }

  .gameTitleContainer {
    @apply flex items-center gap-2;

    height: 20px;

    :global(.favorite-button-wrapper) {
      align-self: center;
    }
  }

  h1 {
    color: #ffffff;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: 20px;
    margin: 0 0 5px;

    .skeleton {
      @apply w-3/5 mb-1.5 pt-4 lg:w-4/5 lg:pt-5;
    }
  }

  .providerName {
    color: #98a2b3;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 13px;
    font-style: normal;
    font-weight: 500;
    line-height: 13px;
    margin: 0;

    .skeleton {
      @apply w-2/5 mb-2 pt-3 lg:w-3/5 lg:pt-3.5 lg:mb-2.5;
    }
  }

  .detailsButtons {
    @apply flex grow-0 gap-2 items-center;

    .skeleton {
      @apply w-8 h-6;
    }

    button {
      @apply p-2.5 m-0 border-0 bg-transparent text-texts text-lg hover:opacity-80;

      svg {
        @apply w-4 h-4 text-texts md:w-5 md:h-5;
      }
    }

    .shareIcons button.shareIconBtn,
    button.fullscreenBtn {
      @apply flex items-center justify-center w-8 h-8 rounded-full bg-white/10 p-0 text-white hover:bg-white/20 hover:opacity-100 transition-colors;

      svg {
        @apply w-[18px] h-[18px] text-white md:w-[18px] md:h-[18px];
      }
    }

    .shareIcons button.shareIconBtn :global(.nuxt-icon),
    .shareIcons button.shareIconBtn :global(.nuxt-icon svg),
    .shareIcons button.shareIconBtn svg {
      filter: grayscale(1);
      opacity: 0.8;
    }
  }

  .equityDialog {
    @apply border-none outline-none bg-bg-primary p-6 text-texts;

    margin: revert-layer;

    &::backdrop {
      @apply bg-black/70;
    }
  }

  .equityValue {
    @apply min-h-16 grid place-content-center;
  }
}
</style>
